import React, { useState, useEffect, useRef, useId } from "react";
import * as ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useReAuthStore, ReAuthReason } from "@/stores/auth/reauth-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { Shield, LogOut, Key, ArrowLeft, AlertCircle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { saveBeforeNavigate } from "@/utils/safeNavigation";
import { reconnectAgentSocket } from "@/services/api/agentSocketClient";
import { securityService } from "@/services/security/securityService";
import {
  getSignInErrorMessage,
  getTwoFactorErrorMessage,
} from "@/utils/authErrorMessages";

const getReasonMessage = (reason: ReAuthReason): { title: string; description: string } => {
  switch (reason) {
    case 'session_expired':
      return {
        title: "Session Expired",
        description: "Your session has expired. Please sign in again to continue.",
      };
    case 'token_invalid':
      return {
        title: "Authentication Required",
        description: "Your authentication has become invalid. Please sign in to continue.",
      };
    case 'refresh_failed':
      return {
        title: "Session Refresh Failed",
        description: "We couldn't refresh your session automatically. Please sign in again.",
      };
    default:
      return {
        title: "Sign In Required",
        description: "Please sign in to continue.",
      };
  }
};

export const ReAuthModal: React.FC = () => {
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);

  const passwordInputRef = useRef<HTMLInputElement>(null);
  const twoFactorInputRef = useRef<HTMLInputElement>(null);
  const emailInputId = useId();
  const passwordInputId = useId();
  const twoFactorInputId = useId();

  const {
    isOpen,
    reason,
    lastEmail,
    closeModal,
    resolvePendingRequests,
    rejectPendingRequests,
  } = useReAuthStore();

  const {
    user,
    login,
    verify2FA,
    cancel2FA,
    requires2FA,
    logout,
  } = useAuthStore();

  // Get email from current user or last known email
  const email = user?.email || lastEmail || "";

  // Mount state for portal rendering
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Focus password input when modal opens
  useEffect(() => {
    if (isOpen && !requires2FA && passwordInputRef.current) {
      // Small delay to ensure the modal is rendered
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, requires2FA]);

  // Focus 2FA input when switching to 2FA step
  useEffect(() => {
    if (requires2FA && twoFactorInputRef.current) {
      twoFactorInputRef.current.focus();
    }
  }, [requires2FA]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setTwoFactorCode("");
      setUseBackupCode(false);
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await login({ identifier: email, password });

      // If 2FA is not required, login is complete
      if (!useAuthStore.getState().requires2FA) {
        // Success! Resolve pending requests and close modal
        securityService.resetAuthenticatedSession("reauth_success");
        resolvePendingRequests();
        reconnectAgentSocket();
        closeModal();
      }
      // If 2FA is required, the requires2FA state will be set and we show the 2FA form
    } catch (err) {
      setError(getSignInErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await verify2FA(twoFactorCode, useBackupCode ? 'backup_code' : '2fa');

      // Success! Resolve pending requests and close modal
      securityService.resetAuthenticatedSession("reauth_success");
      resolvePendingRequests();
      reconnectAgentSocket();
      closeModal();
    } catch (err) {
      setError(getTwoFactorErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTwoFactorCode("");
    setUseBackupCode(false);
    setError("");
  };

  const handleLogout = async () => {
    // User chose to fully logout - reject all pending requests
    rejectPendingRequests(new Error("User logged out"));
    closeModal();
    // Save editor work before navigating away
    await saveBeforeNavigate();
    await logout();
    // Redirect to login page
    window.location.href = "/login";
  };

  // Don't render if not open or not mounted
  if (!isOpen || !mounted) return null;

  const { title, description } = getReasonMessage(reason);

  const content = (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      {/* Dark overlay backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998]" />

      {/* Centering container */}
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Modal content */}
        <div
          className={cn(
            "relative z-[10000] w-full max-w-lg rounded-xl shadow-2xl",
            "bg-card text-card-foreground",
            "border border-border",
            "animate-in fade-in-0 zoom-in-95 duration-200"
          )}
        >
          {/* Accent bar */}
          <div className="h-1 w-full rounded-t-xl bg-gradient-to-r from-primary to-primary/60" />

          {/* Header */}
          <div className="px-8 pt-7 pb-5">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                {reason === 'session_expired' ? (
                  <Clock className="h-5 w-5 text-primary" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-primary" />
                )}
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-semibold tracking-tight text-foreground">
                  {requires2FA ? "Two-factor authentication" : title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {requires2FA
                    ? useBackupCode
                      ? "Enter one of your backup codes"
                      : "Enter the 6-digit code from your authenticator app"
                    : description}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-8 border-t border-border" />

          {/* Content */}
          <div className="px-8 py-6">
            {requires2FA ? (
              // 2FA Form
              <form onSubmit={handle2FASubmit} className="space-y-5">
                {error && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm"
                  >
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor={twoFactorInputId}
                    className="text-sm font-medium text-foreground"
                  >
                    {useBackupCode ? "Backup code" : "Authentication code"}
                  </label>
                  <Input
                    id={twoFactorInputId}
                    ref={twoFactorInputRef}
                    type="text"
                    inputMode={useBackupCode ? "text" : "numeric"}
                    placeholder={useBackupCode ? "Enter backup code" : "000000"}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\s/g, ''))}
                    required
                    disabled={isLoading}
                    maxLength={useBackupCode ? 16 : 6}
                    className={cn(
                      useBackupCode ? "" : "text-center text-2xl tracking-widest font-mono",
                      "h-11 bg-background"
                    )}
                    autoComplete="one-time-code"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={isLoading || !twoFactorCode || (!useBackupCode && twoFactorCode.length !== 6)}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {isLoading ? "Verifying..." : "Verify & continue"}
                </Button>

                <div className="flex flex-col gap-1.5 pt-3 border-t border-border">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUseBackupCode(!useBackupCode);
                      setTwoFactorCode("");
                      setError("");
                    }}
                    className="w-full text-muted-foreground"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {useBackupCode ? "Use authenticator app instead" : "Use a backup code"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel2FA}
                    className="w-full text-muted-foreground"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to password
                  </Button>
                </div>
              </form>
            ) : (
              // Password Form
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm"
                  >
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor={emailInputId}
                    className="text-sm font-medium text-foreground"
                  >
                    Email
                  </label>
                  <Input
                    id={emailInputId}
                    type="email"
                    value={email}
                    disabled
                    className="h-11 bg-muted text-muted-foreground"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor={passwordInputId}
                    className="text-sm font-medium text-foreground"
                  >
                    Password
                  </label>
                  <Input
                    id={passwordInputId}
                    ref={passwordInputRef}
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-11 bg-background"
                    autoComplete="current-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-[15px]"
                  disabled={isLoading || !password}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {isLoading ? "Signing in..." : "Continue session"}
                </Button>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-border bg-muted/40 rounded-b-xl">
            <Button
              type="button"
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out instead
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default ReAuthModal;
